import { Copy, WarningCircle } from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { isApiClientError, shortRequestId } from '@sm/api';

import { cx } from './cx';

export function Spinner({ className, label }: { className?: string; label?: string }) {
  const { t } = useTranslation();
  return (
    <span
      role="status"
      aria-label={label ?? t('panel:states.loading')}
      className={cx('inline-flex items-center gap-2 text-sm text-muted', className)}
    >
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-line2 border-t-accent" />
    </span>
  );
}

export function Loading({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <div
      className={cx('flex items-center justify-center gap-3 p-10 text-sm text-muted', className)}
    >
      <Spinner />
      {t('panel:states.loading')}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <span aria-hidden className={cx('block animate-pulse rounded-md bg-bg3', className)} />;
}

export function Empty({
  icon,
  children,
  className,
}: {
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cx(
        'flex flex-col items-center gap-2 px-4 py-9 text-center text-sm text-muted',
        className,
      )}
    >
      {icon}
      {children}
    </div>
  );
}

export function ErrorState({
  error,
  onRetry,
  className,
}: {
  error: unknown;
  onRetry?: () => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const msg = isApiClientError(error) ? t(error.i18nKey) : t('panel:states.error.description');
  return (
    <div
      role="alert"
      className={cx('flex flex-col items-center gap-3 px-4 py-10 text-center', className)}
    >
      <WarningCircle size={28} className="text-danger" />
      <div className="flex flex-col gap-1">
        <span className="font-heading text-base font-semibold">
          {t('panel:states.error.title')}
        </span>
        <span className="text-sm text-muted">{msg}</span>
      </div>
      {isApiClientError(error) && error.showsRequestId ? (
        <RequestIdBadge requestId={error.requestId} />
      ) : null}
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="min-h-[38px] rounded-md border border-line2 bg-surface px-3.5 text-[13px] font-semibold hover:border-accent hover:text-accent"
        >
          {t('common:actions.retry')}
        </button>
      ) : null}
    </div>
  );
}

/** Kopira tekst u međuspremnik (bez greške ako pregledač to ne dozvoli). */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * „Šifra greške: ab12cd34“ sa dugmetom za kopiranje punog request ID-ja (podrška ga traži u logu i Sentryju).
 */
export function RequestIdBadge({
  requestId,
  tone = 'muted',
  onCopied,
}: {
  requestId: string | undefined;
  tone?: 'muted' | 'inverse';
  onCopied?: () => void;
}) {
  const { t } = useTranslation();
  const code = shortRequestId(requestId);
  if (!requestId || !code) return null;
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1.5 text-xs',
        tone === 'inverse' ? 'text-white/75' : 'text-muted',
      )}
      data-testid="request-id"
    >
      <span className="select-all font-mono" title={requestId}>
        {t('common:support.errorCode', { code })}
      </span>
      <button
        type="button"
        aria-label={t('common:support.copyCode')}
        title={t('common:support.copyCode')}
        onClick={() => void copyText(requestId).then((ok) => ok && onCopied?.())}
        className={cx(
          'grid h-6 w-6 place-items-center rounded-sm',
          tone === 'inverse' ? 'text-white/60 hover:text-white' : 'hover:text-accent',
        )}
      >
        <Copy size={13} />
      </button>
    </span>
  );
}
